(() => {
  'use strict';

  const SELECTORS = {
    controls: '[data-lamore-infinite]',
    sentinel: '[data-lamore-sentinel]',
    loading: '[data-lamore-loading]',
    loadMore: '[data-lamore-load-more]',
    end: '[data-lamore-end]',
    nextLink: 'link[rel="next"], .pagination__item--next a, .pagination .next a, a[rel="next"]'
  };

  class LamoreInfiniteScroll {
    constructor(controls) {
      this.controls = controls;
      this.gridSelector = controls.dataset.gridSelector || '#product-grid';
      this.grid = document.querySelector(this.gridSelector);
      if (!this.grid) return;

      this.sentinel = controls.querySelector(SELECTORS.sentinel);
      this.loading = controls.querySelector(SELECTORS.loading);
      this.loadMoreBtn = controls.querySelector(SELECTORS.loadMore);
      this.endState = controls.querySelector(SELECTORS.end);

      this.nextUrl = this.grid.dataset.nextUrl || this.getNextFromDocument(document);
      this.isLoading = false;
      this.isDone = !this.nextUrl;
      this.abortController = null;
      this.observer = null;

      this.bind();
      this.observe();
      this.renderUi();
    }

    bind() {
      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', () => this.loadNextPage());
      }
    }

    observe() {
      if (!this.sentinel || this.isDone) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { root: null, rootMargin: '280px 0px 280px 0px', threshold: 0.01 }
      );
      this.observer.observe(this.sentinel);
    }

    getNextFromDocument(doc) {
      const node = doc.querySelector(SELECTORS.nextLink);
      return node ? node.getAttribute('href') : null;
    }

    setLoading(state) {
      this.isLoading = state;
      if (this.loading) this.loading.hidden = !state;
      this.renderUi();
    }

    renderUi() {
      if (this.loadMoreBtn) this.loadMoreBtn.hidden = this.isLoading || this.isDone;
      if (this.endState) this.endState.hidden = !this.isDone;
    }

    makeSkeletons(count = 4) {
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < count; i += 1) {
        const item = document.createElement('li');
        item.className = 'grid__item lamore-skeleton-wrap';
        item.innerHTML = '<div class="lamore-skeleton" aria-hidden="true"></div>';
        fragment.appendChild(item);
      }
      this.grid.appendChild(fragment);
    }

    clearSkeletons() {
      this.grid.querySelectorAll('.lamore-skeleton-wrap').forEach((node) => node.remove());
    }

    runBackgroundTask(task) {
      return new Promise((resolve) => {
        const run = () => resolve(task());
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: 150 });
        } else {
          setTimeout(run, 0);
        }
      });
    }

    appendInChunks(nodes) {
      return new Promise((resolve) => {
        const queue = [...nodes];
        const appendChunk = () => {
          if (!queue.length) {
            resolve();
            return;
          }
          const fragment = document.createDocumentFragment();
          for (let i = 0; i < 8 && queue.length; i += 1) fragment.appendChild(queue.shift());
          this.grid.appendChild(fragment);
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(appendChunk, { timeout: 120 });
          } else {
            setTimeout(appendChunk, 16);
          }
        };
        appendChunk();
      });
    }

    async fetchNextPage() {
      if (!this.nextUrl) return '';
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const response = await fetch(this.nextUrl, {
        signal: this.abortController.signal,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    }

    async loadNextPage() {
      if (this.isLoading || this.isDone || !this.nextUrl) return;
      this.setLoading(true);
      this.makeSkeletons(4);

      try {
        const html = await this.fetchNextPage();
        const payload = await this.runBackgroundTask(() => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const incomingGrid = doc.querySelector(this.gridSelector);
          if (!incomingGrid) return { nextUrl: null, items: [] };
          const items = Array.from(incomingGrid.querySelectorAll('.grid__item'))
            .filter((node) => !node.classList.contains('wbimgbnrblock'));
          return {
            nextUrl: incomingGrid.dataset.nextUrl || this.getNextFromDocument(doc),
            items
          };
        });

        payload.items.forEach((item) => item.classList.add('lamore-card-enter'));
        await this.appendInChunks(payload.items);

        requestAnimationFrame(() => {
          payload.items.forEach((item, index) => {
            setTimeout(() => item.classList.add('lamore-card-enter-active'), index * 35);
          });
        });

        this.nextUrl = payload.nextUrl;
        this.isDone = !this.nextUrl;

        if (window.initAllCpCountdowns) window.initAllCpCountdowns();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[Lamore Infinite Scroll]', error);
        }
      } finally {
        this.clearSkeletons();
        this.setLoading(false);
      }
    }
  }

  function initLamoreInfiniteScroll() {
    document.querySelectorAll(SELECTORS.controls).forEach((controls) => {
      if (controls.dataset.lamoreInit === '1') return;
      controls.dataset.lamoreInit = '1';
      new LamoreInfiniteScroll(controls);
    });
  }

  document.addEventListener('DOMContentLoaded', initLamoreInfiniteScroll);
  document.addEventListener('shopify:section:load', initLamoreInfiniteScroll);
})();
